// Qué decirle al analista cuando el listado de clientes falla, y si tiene sentido ofrecerle
// reintentar. Un 403 no se arregla insistiendo: ofrecer el botón sería mandarlo a una pared.
import { ApiError } from '@/api/errors';
import type { Gap } from './customer360Gaps';

export interface ListErrorCopy {
    title: string;
    description: string;
    /** Falso: no se pinta el botón Reintentar porque insistir no cambia el resultado. */
    canRetry: boolean;
}

/** En qué quedó la ÚNICA lectura que arma la tabla: `GET /backoffice/credit-customers`. */
export type CustomerListState = 'loading' | 'ready' | 'error' | 'forbidden';

function isForbidden(error: Error): boolean {
    return error instanceof ApiError && error.code === 'PERMISSION_DENIED';
}

/** El estado de la lista a partir de la query. Un 403 se separa: insistir no lo cambia. */
export function listStateOf(input: {
    /** La primera página todavía viaja. Las siguientes NO cuentan: la tabla ya tiene filas. */
    isPending: boolean;
    error: Error | null;
}): CustomerListState {
    if (input.isPending) return 'loading';
    if (!input.error) return 'ready';
    return isForbidden(input.error) ? 'forbidden' : 'error';
}

/** Qué decir cuando la lectura del PADRÓN falló, y si insistir puede cambiar algo. */
export function listErrorCopy(error: Error): ListErrorCopy {
    if (isForbidden(error)) {
        return {
            title: 'No tienes permiso para ver el padrón de clientes',
            description:
                'Tu rol no incluye el listado de clientes. Si crees que es un error, contacta a quien administra los roles.',
            canRetry: false,
        };
    }
    return {
        title: 'No se pudo cargar el padrón de clientes',
        description: error.message,
        canRetry: true,
    };
}

/** Qué decir cuando la lista volvió leída y sin una sola fila. */
export function emptyListNote(input: { hasFilters: boolean }): string {
    if (input.hasFilters) return 'Sin clientes que coincidan con los filtros.';
    return 'Sin clientes en el padrón.';
}

/**
 * En qué quedó una lectura de la ficha. `not-found` es un HECHO —ese cliente no existe— y no una
 * falla: ofrecerle reintentar mandaría a insistir contra algo que no va a aparecer.
 */
export type ReadFailure = 'forbidden' | 'not-found' | 'error';

export function readFailureOf(error: unknown): ReadFailure | null {
    if (!error) return null;
    if (error instanceof ApiError) {
        if (error.code === 'PERMISSION_DENIED') return 'forbidden';
        if (error.httpStatus === 404) return 'not-found';
    }
    return 'error';
}

/**
 * La ficha 360 tiene su propio permiso (`retail.customer.summary.read`, sin scope): decir su 403
 * con el texto del padrón manda a revisar el rol equivocado.
 */
export function summaryErrorCopy(failure: ReadFailure): ListErrorCopy {
    if (failure === 'forbidden') {
        return {
            title: 'No tienes permiso para ver la ficha de este cliente',
            description:
                'Tu rol no incluye el resumen 360 del cliente. Insistir no lo cambia; contacta a quien administra los roles.',
            canRetry: false,
        };
    }
    if (failure === 'not-found') {
        return {
            title: 'Este cliente ya no está en el padrón',
            description:
                'El silo no encuentra la ficha de este cliente: se dio de baja, o el enlace apunta a un id que nunca existió. La lectura salió bien.',
            canRetry: false,
        };
    }
    return {
        title: 'No se pudo leer la ficha de este cliente',
        description: 'La lectura del resumen 360 falló. Vuelve a intentar.',
        canRetry: true,
    };
}

/**
 * Por qué la TIENDA DE ALTA se queda sin nombre. Es una cuarta lectura, contra el directorio de
 * negocios, y falla por su cuenta: la ficha entera puede estar bien y esta celda no. El hero y la
 * tarjeta Origen leen de aquí, para que la misma ausencia no se lea distinta en la misma pantalla.
 */
const ORIGIN_STORE_FAILURE_LABEL: Record<ReadFailure, string> = {
    forbidden: 'Sin permiso al directorio',
    'not-found': 'La tienda ya no está',
    error: 'No se leyó el directorio',
};

export function originStoreFailureNote(failure: ReadFailure): string {
    if (failure === 'forbidden') {
        return 'Tu rol no incluye la lectura del directorio de tiendas, así que la de alta se queda sin nombre. Es un permiso aparte del de la ficha; contacta a quien administra los roles.';
    }
    if (failure === 'not-found') {
        return 'La tienda donde se dio de alta a este cliente ya no está en el directorio del negocio: se eliminó después del alta, así que su nombre ya no se puede resolver.';
    }
    return 'No se pudo leer el directorio de tiendas, así que la de alta se queda sin nombre. El resto de la ficha se leyó bien.';
}

/** El hueco de la tienda de alta cuando el directorio no se pudo leer, con su desenlace. */
export function originStoreFailureGap(failure: ReadFailure): Gap {
    return { label: ORIGIN_STORE_FAILURE_LABEL[failure], note: originStoreFailureNote(failure) };
}

const SUMMARY_FIELD_FAILURE_LABEL: Record<ReadFailure, string> = {
    forbidden: 'Sin permiso, ni se pidió',
    'not-found': 'La ficha ya no existe',
    error: 'La lectura de la ficha falló',
};

/**
 * Por qué un campo de la ficha 360 se quedó SIN LEER. La tarjeta de identidad sobrevive al fallo
 * —el nombre y el teléfono ya venían del padrón—, así que sus otros campos tienen que decir que no
 * se leyeron: pintarlos como huecos del alta acusa al registro de algo que nadie llegó a mirar.
 */
export function summaryFieldFailureNote(failure: ReadFailure): string {
    if (failure === 'forbidden') {
        return 'Tu rol no incluye la ficha 360 de este cliente, así que este dato ni se pidió. No falta en el alta: no se leyó.';
    }
    if (failure === 'not-found') {
        return 'Este cliente ya no está en el padrón, así que su ficha 360 no devolvió nada. De este dato no se puede afirmar ni que está ni que falta.';
    }
    return 'La lectura de la ficha 360 falló, así que este dato no llegó. No se sabe si el alta lo registró.';
}

/** El hueco de un campo de la ficha 360 que no se leyó, con el desenlace que lo dejó así. */
export function summaryFieldFailureGap(failure: ReadFailure): Gap {
    return { label: SUMMARY_FIELD_FAILURE_LABEL[failure], note: summaryFieldFailureNote(failure) };
}
